/**
 * "Notify me" capture for coming-soon products.
 *
 * On submit the email goes to three places, and all three have to land:
 *   1. Datajet, via the app proxy. It records consent and — on the first
 *      submission for a product — creates the Klaviyo segment that the launch
 *      email is sent to.
 *   2. Klaviyo, appending this product to Coming_Soon_Product_Id on the profile.
 *      That property is what the segment filters on.
 *   3. Klaviyo, as a "Subscribed to Launch" event, for reporting and flows.
 *
 * Steps 2 and 3 are separate calls on purpose. An event sets profile properties
 * outright, so sending the product id there replaced any product the shopper was
 * already waiting on — silently dropping them out of that earlier segment and
 * out of its launch email. Only the profile endpoint can append to an array.
 *
 * All three must succeed before we tell the shopper they are on the list: any
 * one failing leaves them subscribed to nothing they can act on.
 *
 * A fourth call subscribes the shopper to the marketing list, when one is
 * configured. It is deliberately outside that all-or-nothing rule — see
 * subscribeToMarketing.
 */

(function () {
  'use strict';

  const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/events/';
  const KLAVIYO_PROFILE_ENDPOINT = 'https://a.klaviyo.com/client/profiles/';
  const KLAVIYO_SUBSCRIPTION_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  const KLAVIYO_REVISION = '2025-01-15';

  // Deliberately loose: the input is type="email" so the browser has already
  // rejected the worst input, and an over-strict pattern rejects valid
  // addresses. The real check is whether Klaviyo accepts it.
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (customElements.get('coming-soon-notify-form')) return;

  class ComingSoonNotifyForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('.coming-soon-notify__form');
      this.emailInput = this.querySelector('input[type="email"]');
      this.errorEl = this.querySelector('.coming-soon-notify__error');
      this.successEl = this.querySelector('.coming-soon-notify__success');
      this.failureEl = this.querySelector('.coming-soon-notify__failure');
      this.submitButton = this.querySelector('button[type="submit"]');
      this.intro = this.querySelector('.coming-soon-notify__intro');

      if (!this.form || !this.emailInput) return;

      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.emailInput.addEventListener('input', () => this.hideError());
    }

    async onSubmit(event) {
      event.preventDefault();

      const email = this.emailInput.value.trim();
      if (!EMAIL_PATTERN.test(email)) {
        this.showError();
        this.emailInput.focus();
        return;
      }

      this.hideError();
      this.setBusy(true);

      try {
        // Datajet runs first because it creates the per-product Klaviyo segment
        // on the first submission for a product, as well as recording consent.
        // If it fails there is no segment for the profile to land in.
        const datajetOk = await this.sendToDatajet(email);
        // The profile must carry the product before the event fires, so the
        // shopper is already a segment member by the time anything reacts to it.
        const profileOk = await this.appendProductToProfile(email);
        const klaviyoOk = await this.sendToKlaviyo(email);
        // Result is not part of the verdict below, on purpose.
        await this.subscribeToMarketing(email);
        this.showResult(datajetOk && profileOk && klaviyoOk);
      } catch (error) {
        console.error('[coming-soon] Notify me submission failed:', error);
        this.showResult(false);
      } finally {
        // Runs on every path, so the button never stays stuck disabled.
        this.setBusy(false);
      }
    }

    async sendToDatajet(email) {
      const scriptId = this.dataset.datajetScriptId;
      if (!scriptId) {
        console.warn('[coming-soon] No Datajet script ID configured; skipping.');
        return false;
      }

      try {
        const response = await fetch(`/apps/datajet/task/${scriptId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: email,
            email_subscribed: true,
            product_id: this.dataset.productId,
            // One store, two markets: product_id alone cannot tell AU from NZ.
            // Ignored until the script names segments per market.
            country: this.dataset.country,
          }),
        });
        return response.ok;
      } catch (error) {
        console.error('[coming-soon] Datajet error:', error);
        return false;
      }
    }

    /**
     * Appends this product to the profile's Coming_Soon_Product_Id array, and
     * sets the market and local price alongside it.
     *
     * `append` is the whole point of using this endpoint rather than the event:
     * a shopper can wait on several launches at once, and each has its own
     * segment reading this same array.
     *
     * Market, price and URL are single values, so they are simply set. A profile
     * is one person while a market is a property of the browsing session, so
     * these hold whatever was true on the last sign-up: someone waiting on two
     * products, or who signed up from both storefronts, keeps only the newer
     * set. Accepted deliberately — the consequence is a wrong price shown in the
     * email, while the link still lands them on their own storefront. Fixing it
     * properly means carrying the data per (person, product) inside the array
     * above, which is a Datajet and Flow change rather than a theme one.
     */
    async appendProductToProfile(email) {
      const companyId = this.dataset.klaviyoCompanyId;
      if (!companyId) {
        console.error('[coming-soon] No Klaviyo company ID configured.');
        return false;
      }

      const productId = this.dataset.productId;
      if (!productId) {
        console.error('[coming-soon] No product ID; cannot join the segment.');
        return false;
      }

      const properties = {};
      if (this.dataset.country) properties.Coming_Soon_Market = this.dataset.country;
      if (this.dataset.price) properties.Coming_Soon_Price = this.dataset.price;
      // Already carries the market's path prefix, e.g. /en-nz. The launch event
      // builds its own link from the primary domain and cannot know the market,
      // so it sends every New Zealand shopper to the Australian page.
      if (this.dataset.productUrl) properties.Coming_Soon_Product_URL = this.dataset.productUrl;

      try {
        const response = await fetch(
          `${KLAVIYO_PROFILE_ENDPOINT}?company_id=${encodeURIComponent(companyId)}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              revision: KLAVIYO_REVISION,
            },
            body: JSON.stringify({
              data: {
                type: 'profile',
                attributes: { email: email, properties: properties },
                meta: {
                  patch_properties: {
                    append: { Coming_Soon_Product_Id: productId },
                  },
                },
              },
            }),
          }
        );

        // 202 on update, 201 when the profile did not exist yet.
        if (response.status !== 202 && response.status !== 201) {
          console.error('[coming-soon] Klaviyo rejected the profile:', response.status);
          return false;
        }
        return true;
      } catch (error) {
        console.error('[coming-soon] Klaviyo profile error:', error);
        return false;
      }
    }

    /**
     * Subscribes the shopper to the marketing list, recording an explicit
     * email marketing consent against their profile.
     *
     * Klaviyo has no client-side way to set consent without a list, so this is
     * skipped entirely until a list ID is configured, and skipping is silent —
     * a store that does not want launch sign-ups in marketing simply leaves the
     * setting blank rather than seeing a warning on every submission.
     *
     * Its result is not folded into the success verdict. The shopper is already
     * on the launch segment by this point through the three calls above, so
     * failing here would show them an error for something that did work, and
     * push them to submit again. The failure is logged instead.
     */
    async subscribeToMarketing(email) {
      const listId = this.dataset.klaviyoListId;
      if (!listId) return false;

      const companyId = this.dataset.klaviyoCompanyId;
      if (!companyId) {
        console.error('[coming-soon] No Klaviyo company ID configured.');
        return false;
      }

      try {
        const response = await fetch(
          `${KLAVIYO_SUBSCRIPTION_ENDPOINT}?company_id=${encodeURIComponent(companyId)}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              revision: KLAVIYO_REVISION,
            },
            body: JSON.stringify({
              data: {
                type: 'subscription',
                attributes: {
                  // Shows on the profile's consent record, so a later audit can
                  // see which form the person actually agreed on.
                  custom_source: 'Coming Soon Notify Me',
                  profile: {
                    data: {
                      type: 'profile',
                      attributes: {
                        email: email,
                        subscriptions: {
                          email: { marketing: { consent: 'SUBSCRIBED' } },
                        },
                      },
                    },
                  },
                },
                relationships: {
                  list: { data: { type: 'list', id: listId } },
                },
              },
            }),
          }
        );

        // 202 Accepted on success; Klaviyo uses no other 2xx here.
        if (response.status !== 202) {
          console.error('[coming-soon] Klaviyo rejected the subscription:', response.status);
          return false;
        }
        return true;
      } catch (error) {
        console.error('[coming-soon] Klaviyo subscription error:', error);
        return false;
      }
    }

    async sendToKlaviyo(email) {
      const companyId = this.dataset.klaviyoCompanyId;
      if (!companyId) {
        console.error('[coming-soon] No Klaviyo company ID configured.');
        return false;
      }

      const productId = this.dataset.productId;

      try {
        const response = await fetch(
          `${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(companyId)}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              revision: KLAVIYO_REVISION,
            },
            body: JSON.stringify({
              data: {
                type: 'event',
                attributes: {
                  profile: {
                    data: {
                      type: 'profile',
                      attributes: {
                        // Deliberately carries no profile properties.
                        // appendProductToProfile has already written them, and
                        // an event overwrites rather than appends: repeating
                        // Coming_Soon_Product_Id here would flatten the array
                        // back to this one product.
                        email: email,
                      },
                    },
                  },
                  metric: {
                    data: {
                      type: 'metric',
                      attributes: { name: 'Subscribed to Launch' },
                    },
                  },
                  properties: {
                    Product_Id: productId,
                    Product_Name: this.dataset.productTitle,
                    Product_Handle: this.dataset.productHandle,
                    Product_URL: this.dataset.productUrl,
                    Product_Image_URL: this.dataset.productImage,
                    Variant_Id: this.dataset.variantId,
                    Launch_Date: this.dataset.launchDate,
                    Country_Code: this.dataset.country,
                    Currency: this.dataset.currency,
                    Subscription_Source: 'Product Page Modal',
                  },
                  time: new Date().toISOString(),
                },
              },
            }),
          }
        );

        // Klaviyo answers 202 Accepted on success. Anything else is a failure,
        // including the 2xx codes it does not use.
        if (response.status !== 202) {
          console.error('[coming-soon] Klaviyo rejected the event:', response.status);
          return false;
        }
        return true;
      } catch (error) {
        console.error('[coming-soon] Klaviyo error:', error);
        return false;
      }
    }

    showResult(succeeded) {
      const panel = succeeded ? this.successEl : this.failureEl;
      if (!panel) return;

      this.form.hidden = true;
      if (this.intro) this.intro.hidden = true;
      panel.hidden = false;
      panel.focus?.();
    }

    setBusy(isBusy) {
      if (!this.submitButton) return;
      this.submitButton.disabled = isBusy;
      this.submitButton.classList.toggle('loading', isBusy);
    }

    showError() {
      if (this.errorEl) this.errorEl.hidden = false;
      this.emailInput.setAttribute('aria-invalid', 'true');
    }

    hideError() {
      if (this.errorEl) this.errorEl.hidden = true;
      this.emailInput.removeAttribute('aria-invalid');
    }
  }

  customElements.define('coming-soon-notify-form', ComingSoonNotifyForm);

  /**
   * Opens the modal. Delegated from the document so triggers injected later —
   * quick-add, section re-renders — work without rebinding.
   */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-coming-soon-open]');
    if (!trigger) return;

    const modal = document.getElementById(trigger.dataset.comingSoonOpen);
    if (modal && typeof modal.show === 'function') modal.show(trigger);
  });
})();
